import * as THREE from 'three';
import { PhysicsBody, CollisionEvent, GravityConfig, SimulationConfig } from '../types';
import { DEFAULT_SIMULATION_CONFIG } from '../constants';

/**
 * Physics simulation for orbital mechanics and collisions.
 * Uses Verlet integration for stable orbital dynamics.
 */
export class PhysicsWorld {
  private bodies: Map<string, PhysicsBody> = new Map();
  private config: SimulationConfig;
  private collisionCallbacks: ((event: CollisionEvent) => void)[] = [];

  // Accumulated time for fixed timestep
  private accumulator: number = 0;
  private readonly fixedDeltaTime: number = 1 / 60;

  constructor(config: Partial<SimulationConfig> = {}) {
    this.config = { ...DEFAULT_SIMULATION_CONFIG, ...config };
  }

  public addBody(body: PhysicsBody): void {
    this.bodies.set(body.id, body);
  }

  public removeBody(id: string): void {
    this.bodies.delete(id);
  }

  public getBody(id: string): PhysicsBody | undefined {
    return this.bodies.get(id);
  }

  public getBodies(): PhysicsBody[] {
    return Array.from(this.bodies.values());
  }

  public update(deltaTime: number): void {
    if (this.config.paused) return;

    // Apply time scale
    const scaledDelta = deltaTime * this.config.timeScale;

    // Fixed timestep accumulator
    this.accumulator += scaledDelta;

    while (this.accumulator >= this.fixedDeltaTime) {
      this.fixedUpdate(this.fixedDeltaTime);
      this.accumulator -= this.fixedDeltaTime;
    }

    // Interpolate visual positions
    this.syncVisuals();
  }

  private fixedUpdate(dt: number): void {
    // Calculate gravitational forces
    if (this.config.gravity.enabled) {
      this.calculateGravity();
    }

    // Integrate motion
    this.integrateMotion(dt);

    // Handle collisions
    if (this.config.collisionsEnabled) {
      this.detectCollisions();
    }
  }

  private calculateGravity(): void {
    const G = this.config.gravity.G;
    const softening = this.config.gravity.softening;
    const bodies = Array.from(this.bodies.values());

    // Reset accelerations
    bodies.forEach((body) => {
      if (!body.isStatic) {
        body.acceleration.set(0, 0, 0);
      }
    });

    // N-body gravity calculation
    for (let i = 0; i < bodies.length; i++) {
      const bodyA = bodies[i];
      if (bodyA.isStatic) continue;

      for (let j = 0; j < bodies.length; j++) {
        if (i === j) continue;

        const bodyB = bodies[j];

        // Calculate direction and distance
        const direction = new THREE.Vector3()
          .subVectors(bodyB.position, bodyA.position);

        const distanceSq = direction.lengthSq() + softening * softening;
        const distance = Math.sqrt(distanceSq);

        // Gravitational force magnitude: F = G * m1 * m2 / r^2
        const forceMag = (G * bodyB.mass) / distanceSq;

        // Add acceleration: a = F / m = G * m2 / r^2
        direction.normalize().multiplyScalar(forceMag);
        bodyA.acceleration.add(direction);
      }
    }
  }

  private integrateMotion(dt: number): void {
    // Velocity Verlet integration
    this.bodies.forEach((body) => {
      if (body.isStatic) return;

      // Update position: x += v*dt + 0.5*a*dt^2
      const positionDelta = body.velocity.clone().multiplyScalar(dt);
      positionDelta.add(body.acceleration.clone().multiplyScalar(0.5 * dt * dt));
      body.position.add(positionDelta);

      // Update velocity: v += a*dt
      body.velocity.add(body.acceleration.clone().multiplyScalar(dt));
    });
  }

  private detectCollisions(): void {
    const bodies = Array.from(this.bodies.values());

    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const bodyA = bodies[i];
        const bodyB = bodies[j];

        const direction = new THREE.Vector3()
          .subVectors(bodyB.position, bodyA.position);
        const distance = direction.length();
        const minDistance = bodyA.radius + bodyB.radius;

        if (distance < minDistance) {
          const penetration = minDistance - distance;
          const normal = direction.normalize();

          const collisionPoint = bodyA.position.clone()
            .add(normal.clone().multiplyScalar(bodyA.radius));

          const event: CollisionEvent = {
            bodyA,
            bodyB,
            point: collisionPoint,
            normal,
            penetration,
          };

          // Resolve collision
          this.resolveCollision(event);

          // Notify listeners
          this.collisionCallbacks.forEach((cb) => cb(event));
        }
      }
    }
  }

  private resolveCollision(event: CollisionEvent): void {
    const { bodyA, bodyB, normal, penetration } = event;

    // Skip if both are static
    if (bodyA.isStatic && bodyB.isStatic) return;

    // Separate bodies
    const totalMass = (bodyA.isStatic ? 0 : bodyA.mass) + (bodyB.isStatic ? 0 : bodyB.mass);

    if (!bodyA.isStatic) {
      const ratio = bodyB.isStatic ? 1 : bodyB.mass / totalMass;
      bodyA.position.sub(normal.clone().multiplyScalar(penetration * ratio));
    }

    if (!bodyB.isStatic) {
      const ratio = bodyA.isStatic ? 1 : bodyA.mass / totalMass;
      bodyB.position.add(normal.clone().multiplyScalar(penetration * ratio));
    }

    // Elastic collision response
    const restitution = 0.8; // Bounciness

    // Relative velocity
    const relativeVelocity = new THREE.Vector3()
      .subVectors(bodyB.velocity, bodyA.velocity);

    const velocityAlongNormal = relativeVelocity.dot(normal);

    // Don't resolve if separating
    if (velocityAlongNormal > 0) return;

    // Calculate impulse
    let impulseMag = -(1 + restitution) * velocityAlongNormal;
    impulseMag /= (bodyA.isStatic ? 0 : 1 / bodyA.mass) + (bodyB.isStatic ? 0 : 1 / bodyB.mass);

    const impulse = normal.clone().multiplyScalar(impulseMag);

    if (!bodyA.isStatic) {
      bodyA.velocity.sub(impulse.clone().multiplyScalar(1 / bodyA.mass));
    }

    if (!bodyB.isStatic) {
      bodyB.velocity.add(impulse.clone().multiplyScalar(1 / bodyB.mass));
    }
  }

  private syncVisuals(): void {
    this.bodies.forEach((body) => {
      if (body.object3D) {
        body.object3D.position.copy(body.position);
      }
    });
  }

  // Public API

  public onCollision(callback: (event: CollisionEvent) => void): void {
    this.collisionCallbacks.push(callback);
  }

  public setTimeScale(scale: number): void {
    this.config.timeScale = scale;
  }

  public getTimeScale(): number {
    return this.config.timeScale;
  }

  public setPaused(paused: boolean): void {
    this.config.paused = paused;
  }

  public isPaused(): boolean {
    return this.config.paused;
  }

  public setGravityEnabled(enabled: boolean): void {
    this.config.gravity.enabled = enabled;
  }

  public setGravityConstant(G: number): void {
    this.config.gravity.G = G;
  }

  public setCollisionsEnabled(enabled: boolean): void {
    this.config.collisionsEnabled = enabled;
  }

  public getConfig(): SimulationConfig {
    return { ...this.config };
  }

  /**
   * Calculate stable orbital velocity for a body around another.
   */
  public calculateOrbitalVelocity(
    orbitingBody: PhysicsBody,
    centralBody: PhysicsBody
  ): THREE.Vector3 {
    const G = this.config.gravity.G;
    const direction = new THREE.Vector3()
      .subVectors(orbitingBody.position, centralBody.position);
    const distance = direction.length();

    // Orbital velocity magnitude: v = sqrt(G * M / r)
    const speed = Math.sqrt((G * centralBody.mass) / distance);

    // Get perpendicular direction in the orbital plane
    const up = new THREE.Vector3(0, 1, 0);
    const velocityDirection = new THREE.Vector3()
      .crossVectors(direction.normalize(), up)
      .normalize();

    return velocityDirection.multiplyScalar(speed);
  }

  /**
   * Apply an impulse to a body.
   */
  public applyImpulse(bodyId: string, impulse: THREE.Vector3): void {
    const body = this.bodies.get(bodyId);
    if (body && !body.isStatic) {
      body.velocity.add(impulse.clone().divideScalar(body.mass));
    }
  }

  /**
   * Get total kinetic energy of the system.
   */
  public getTotalKineticEnergy(): number {
    let energy = 0;
    this.bodies.forEach((body) => {
      if (!body.isStatic) {
        energy += 0.5 * body.mass * body.velocity.lengthSq();
      }
    });
    return energy;
  }

  /**
   * Get total potential energy of the system (gravitational).
   */
  public getTotalPotentialEnergy(): number {
    const G = this.config.gravity.G;
    const bodies = Array.from(this.bodies.values());
    let energy = 0;

    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const distance = bodies[i].position.distanceTo(bodies[j].position);
        if (distance > 0) {
          energy -= (G * bodies[i].mass * bodies[j].mass) / distance;
        }
      }
    }

    return energy;
  }

  public clear(): void {
    this.bodies.clear();
  }
}
